import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';

/**
 * Menu "⋯" das ações de uma linha ou cartão (auditoria A1 e M3): as ações perigosas ficam aqui dentro,
 * longe do clique acidental. Fecha com Esc, com clique fora e ao escolher uma opção.
 */
export interface ItemDoMenu {
  rotulo: string;
  icone?: ReactNode;
  aoClicar: () => void;
  perigo?: boolean;
  desabilitado?: boolean;
}

export function RowMenu({ itens, rotulo = 'Mais ações' }: { itens: ItemDoMenu[]; rotulo?: string }) {
  const [aberto, setAberto] = useState(false);
  const raizRef = useRef<HTMLDivElement>(null);
  const botaoRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!aberto) return;
    const aoClicarFora = (e: MouseEvent) => {
      if (raizRef.current && !raizRef.current.contains(e.target as Node)) setAberto(false);
    };
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setAberto(false);
        botaoRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    // Foco no primeiro item, para dar para escolher pelo teclado.
    raizRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not([disabled])')?.focus();
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto]);

  const aoTeclarNoMenu = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const itensDoMenu = Array.from(raizRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])') ?? []);
    const atual = itensDoMenu.indexOf(document.activeElement as HTMLButtonElement);
    const proximo = e.key === 'ArrowDown' ? (atual + 1) % itensDoMenu.length : (atual - 1 + itensDoMenu.length) % itensDoMenu.length;
    itensDoMenu[proximo]?.focus();
  };

  return (
    <div ref={raizRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={botaoRef}
        type="button"
        className="botao-icone"
        aria-label={rotulo}
        title={rotulo}
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-controls={aberto ? menuId : undefined}
        onClick={() => setAberto((v) => !v)}
      >
        <MoreHorizontal size={18} />
      </button>
      {aberto && (
        <div
          id={menuId}
          role="menu"
          onKeyDown={aoTeclarNoMenu}
          style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, minWidth: '180px', background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)', padding: 'var(--space-1)', zIndex: 100 }}
        >
          {itens.map((item) => (
            <button
              key={item.rotulo}
              type="button"
              role="menuitem"
              disabled={item.desabilitado}
              onClick={() => { setAberto(false); botaoRef.current?.focus(); item.aoClicar(); }}
              className="item-do-menu"
              style={{ color: item.perigo ? 'var(--color-error)' : 'var(--color-text-primary)' }}
            >
              {item.icone}
              {item.rotulo}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
